import React from 'react';
import { Alert, Empty, Spin, Typography } from 'antd';
import { istDateTime } from '@/utils/cricketCalendar';
import { ByAppTable } from './ByAppTable';
import { MetricTable } from './MetricTable';
import { SyncTiles } from './SyncTiles';
import { WorstTable } from './WorstTable';
import { fmtNum, type SyncState } from './model';

const { Title, Text } = Typography;

/** How many matches the endpoint reads at most (it answers `capped` beyond this). */
export const SYNC_CAP = 20000;

const Section: React.FC<{ title: string; hint?: string; children: React.ReactNode }> = ({
  title,
  hint,
  children,
}) => (
  <section style={{ marginTop: 24 }}>
    <Title level={5} style={{ marginBottom: 0 }}>
      {title}
    </Title>
    {hint && (
      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
        {hint}
      </Text>
    )}
    {children}
  </section>
);

/** Everything under the controls: the error, or the numbers of the range (or why there are none). */
export const SyncReport: React.FC<{ state: SyncState }> = ({ state }) => {
  const { data, loading, error } = state;
  return (
    <>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
      {!data && loading && <Spin style={{ display: 'block', margin: '24px auto' }} />}
      {data && (
        <Spin spinning={loading}>
          <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
            {istDateTime(data.from)} to {istDateTime(data.to)}
          </Text>
          {data.capped && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 8 }}
              message={`Showing the newest ${fmtNum(SYNC_CAP, 0)} matches`}
              description="The range holds more; narrow it or add a filter to see the rest."
            />
          )}
          {(data.skipped.rows > 0 || data.skipped.hists > 0) && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 8 }}
              message={`${fmtNum(data.skipped.rows, 0)} stored rows and ${fmtNum(
                data.skipped.hists,
                0
              )} histograms could not be read and are left out of these numbers.`}
            />
          )}
          {data.totals.matches === 0 ? (
            <Empty description="No two-phone matches in this range" />
          ) : (
            <>
              <SyncTiles totals={data.totals} check={data.check} />
              <Section
                title="Metrics by role"
                hint="Percentiles over every ball; the batter and the watcher phone are separate rows. A dash means no samples."
              >
                <MetricTable
                  metrics={data.metrics}
                  texts={data.texts}
                  keysCut={data.totals.keysCut}
                />
              </Section>
              <Section
                title="Check latency (ms)"
                hint={`${fmtNum(data.check.n, 0)} checks. A dash means no samples.`}
              >
                <Text>
                  p50 {fmtNum(data.check.p50)} · p95 {fmtNum(data.check.p95)} · p99{' '}
                  {fmtNum(data.check.p99)} · max {fmtNum(data.check.max)}
                </Text>
              </Section>
              <Section
                title="Worst matches"
                hint="Most mismatches first, then the largest start error. At most 50."
              >
                <WorstTable worst={data.worst} />
              </Section>
              <Section
                title="By app"
                hint="The 10 busiest app, build and platform groups. Each build's phones are charged only their own mismatches."
              >
                <ByAppTable rows={data.byApp} />
              </Section>
            </>
          )}
        </Spin>
      )}
    </>
  );
};
